import { Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** Klasa włączająca stan zajętości; jej wygląd i @keyframes siedzą globalnie w styles.scss. */
const BUSY_CLASS = 'app-busy';

/** Warstwa nakładana na treść przycisku. Ta sama nazwa klasy co w styles.scss. */
const OVERLAY_CLASS = 'app-busy-overlay';

/**
 * Ile czekamy, zanim w ogóle pokażemy wskaźnik. Mediana odpowiedzi tego API to
 * kilkadziesiąt milisekund — bez tego progu spinner mrugałby przy każdym zapisie,
 * a migający interfejs sprawia wrażenie niestabilnego bardziej niż brak wskaźnika.
 */
export const BUSY_SHOW_DELAY_MS = 300;

/**
 * Ile wskaźnik zostaje na ekranie, gdy już się pojawi. Odpowiedź, która przyszła
 * tuż po przekroczeniu progu, nie może zgasić go w tej samej klatce.
 */
export const BUSY_MIN_VISIBLE_MS = 400;

/**
 * Stan „kliknięcie przyjęte, pracuję" na przycisku zapisu.
 *
 * Dziś po kliknięciu „Zapisz" przycisk tylko gaśnie przez `[disabled]` — wygląda to jak
 * awaria, a nie jak praca. Ta dyrektywa odpowiada na jedno pytanie użytkownika:
 * *kliknęło się?*. Na pytanie *zapisało się?* odpowiada toast, więc trzeciego stanu
 * („Zapisano" z ptaszkiem) tu nie ma — dublowałby komunikat pokazywany obok.
 *
 * Treść przycisku jest **zasłaniana, nie kasowana**: etykiety bywają złożone (dwa warianty
 * pod `*ngIf`, ikony), a podmiana przez innerHTML gubiłaby je bezpowrotnie.
 *
 * Dyrektywa **nie dotyka `disabled`** — szablony mają własne warunki, czasem złożone
 * (`isSubmitting || getSelectedCount() === 0`), a binding Angulara i tak nadpisałby ją
 * przy każdym cyklu detekcji zmian. Stąd oba atrybuty obok siebie:
 *
 *     <button [disabled]="isSubmitting" [appBusy]="isSubmitting">
 *
 * To nie jest powtórzenie: pierwszy mówi „nie klikaj", drugi „pokaż, że pracuję", i przy
 * złożonych warunkach się rozjeżdżają.
 */
@Directive({
  selector: '[appBusy]'
})
export class BusyButtonDirective implements OnChanges, OnDestroy {

  @Input('appBusy') busy: boolean;

  /**
   * Opcjonalny klucz komunikatu obok spinnera. Domyślnie pusty — na przycisku zostaje samo
   * kręcące się kółko, bo napis dubluje to, co spinner mówi bez słów, i rozpycha przycisk.
   *
   * Podaj klucz tam, gdzie sama animacja nie wystarcza: w oknie potwierdzenia „Tak" traci
   * po zasłonięciu swoją jedyną etykietę, więc `BUTTONS.DELETING` mówi, co się właśnie
   * dzieje. Klucze BUTTONS.SAVING / DELETING / PROCESSING są przetłumaczone we wszystkich
   * dwudziestu językach.
   */
  @Input() appBusyLabel: string = null;

  private overlay: HTMLElement = null;
  private showTimer: any = null;
  private hideTimer: any = null;
  /** Kiedy wskaźnik faktycznie stanął na ekranie — od tego liczy się czas minimalny. */
  private shownAt = 0;

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private translate: TranslateService
  ) { }

  ngOnChanges(): void {
    if (this.busy) {
      this.scheduleEnter();
    } else {
      this.scheduleLeave();
    }
  }

  ngOnDestroy(): void {
    // Przycisk może zniknąć w trakcie zapisu (formularz zamknięty przez inną ścieżkę).
    this.clearTimers();
    this.leave();
  }

  /** Wskaźnik pojawia się dopiero, gdy praca trwa dłużej niż próg. */
  private scheduleEnter(): void {
    if (this.overlay || this.showTimer) {
      return;
    }
    this.clearHideTimer();
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.shownAt = Date.now();
      this.enter();
    }, BUSY_SHOW_DELAY_MS);
  }

  /** Praca się skończyła: albo wskaźnika nigdy nie było, albo musi jeszcze chwilę zostać. */
  private scheduleLeave(): void {
    // Odpowiedź przyszła przed progiem — wskaźnik nie zdążył się pokazać i nie pokaże.
    if (this.showTimer) {
      this.clearShowTimer();
      return;
    }
    if (!this.overlay || this.hideTimer) {
      return;
    }

    const visibleFor = Date.now() - this.shownAt;
    if (visibleFor >= BUSY_MIN_VISIBLE_MS) {
      this.leave();
      return;
    }
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.leave();
    }, BUSY_MIN_VISIBLE_MS - visibleFor);
  }

  private enter(): void {
    const button = this.host.nativeElement;

    // Oba pomiary przed jakąkolwiek zmianą, bo zmiana wpływa na jedno i drugie.
    const width = button.getBoundingClientRect().width;
    const color = getComputedStyle(button).color;

    // Szerokość zablokowana dla przycisków, które same podmieniają swoją etykietę na czas
    // zapisu — bez tego skaczą i przesuwają sąsiedni „Anuluj" pod kursorem użytkownika.
    if (width > 0) {
      this.renderer.setStyle(button, 'min-width', `${Math.ceil(width)}px`);
    }

    // Gasi treść, której nie da się ukryć regułą CSS: gołe węzły tekstowe.
    //
    // Styl bezpośredni, a nie klasa — reguła klasowa remisuje swoistością z `.btn`
    // Bootstrapa i o wyniku decydowałaby kolejność arkuszy. I do tego `!important`,
    // bo Bootstrap ma `.text-white { color: #fff !important }`, a tej klasy używa
    // choćby przycisk „Tak" w oknie potwierdzenia — bez tego stara etykieta
    // prześwitywała spod warstwy. Warstwa dostaje kolor z powrotem niżej.
    this.renderer.setStyle(button, 'color', 'transparent', RendererStyleFlags2.Important);

    this.renderer.addClass(button, BUSY_CLASS);
    this.renderer.setAttribute(button, 'aria-busy', 'true');

    this.overlay = this.createOverlay(color);
    this.renderer.appendChild(button, this.overlay);
  }

  /** Kręcące się kółko, opcjonalnie z komunikatem — w kolorze zdjętym z przycisku. */
  private createOverlay(color: string): HTMLElement {
    const overlay = this.renderer.createElement('span');
    this.renderer.addClass(overlay, OVERLAY_CLASS);
    this.renderer.setStyle(overlay, 'color', color);
    // aria-hidden, bo stan i tak ogłasza aria-busy na przycisku — inaczej czytnik
    // przeczytałby zasłoniętą etykietę i tekst warstwy jeden po drugim.
    this.renderer.setAttribute(overlay, 'aria-hidden', 'true');

    const spinner = this.renderer.createElement('span');
    this.renderer.addClass(spinner, 'app-busy-spinner');
    this.renderer.appendChild(overlay, spinner);

    if (this.appBusyLabel) {
      this.renderer.appendChild(
        overlay,
        this.renderer.createText(this.translate.instant(this.appBusyLabel))
      );
    }

    return overlay;
  }

  private leave(): void {
    const button = this.host.nativeElement;

    if (this.overlay) {
      this.renderer.removeChild(button, this.overlay);
      this.overlay = null;
    }
    this.renderer.removeClass(button, BUSY_CLASS);
    this.renderer.removeAttribute(button, 'aria-busy');
    this.renderer.removeStyle(button, 'min-width');
    this.renderer.removeStyle(button, 'color', RendererStyleFlags2.Important);
  }

  private clearTimers(): void {
    this.clearShowTimer();
    this.clearHideTimer();
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
